import { useMemo } from 'react';
import { Box, Card, Icon, Link, Text, Toggle } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { OrdersAutoCancelPaymentStatusConfiguration } from '@tiendanube/common/src/domains/orders/orders';
import { OrdersAutoCancelPaymentMethod } from '@tiendanube/common/src/enums';
import { OrdersAutoCancelExpirationTime } from '@tiendanube/common/src/types';
import { AlertSessionStorage } from 'App/components';
import { HELP_LINKS_ORDER_RECOVERY } from 'commons/constants';
import { useTranslationLanguage } from 'commons/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import PaymentMethodConfig from './PaymentMethodConfig';
import usePaymentMethodsConfiguration from '../../hooks/usePaymentMethodsConfiguration';
import './PaymentStatusConfig.scss';

interface PaymentMethodsConfigProps {
  configuration: OrdersAutoCancelPaymentStatusConfiguration;
  disabled: boolean;
  onChange: (configuration: OrdersAutoCancelPaymentStatusConfiguration) => void;
  paymentMethodsWithError: OrdersAutoCancelPaymentMethod[];
  pixPreselectedExpirationTime?: OrdersAutoCancelExpirationTime | null;
  defaultPixExpirationTime?: OrdersAutoCancelExpirationTime | null;
}

function PaymentMethodsConfig({
  configuration,
  disabled,
  onChange,
  paymentMethodsWithError = [],
  pixPreselectedExpirationTime = null,
  defaultPixExpirationTime = null,
}: Readonly<PaymentMethodsConfigProps>): JSX.Element {
  const t = useTranslationOrders('autoCancel');
  const language = useTranslationLanguage();
  const url = HELP_LINKS_ORDER_RECOVERY[language];

  const {
    paymentMethodsConfig,
    handleToggleChange,
    handleExpirationTimeChange,
    selectedSubceedsCheckout,
    focusedPaymentMethods,
  } = usePaymentMethodsConfiguration(
    configuration,
    disabled,
    onChange,
    pixPreselectedExpirationTime,
    defaultPixExpirationTime,
  );

  const paymentMethodsWithErrorSet = useMemo(
    () => new Set(paymentMethodsWithError),
    [paymentMethodsWithError],
  );

  return (
    <Card>
      <Card.Header title={t('paymentMethodsTitle')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Text>{t('paymentMethodsDesc')}</Text>
          {Object.entries(paymentMethodsConfig).map(
            ([paymentMethod, config]) => (
              <Box
                gap="2"
                key={`box-${paymentMethod}`}
                display="flex"
                flexDirection="column"
              >
                <div className={disabled ? 'disabled-toggle' : ''}>
                  <Toggle
                    name={`paymentMethodToggle-${paymentMethod}`}
                    label={t(`paymentMethod.${paymentMethod}`)}
                    checked={!!config?.isActive}
                    onChange={(e) => {
                      handleToggleChange(
                        e,
                        paymentMethod as OrdersAutoCancelPaymentMethod,
                        !config?.isActive,
                      );
                    }}
                  />
                </div>
                {!!config?.isActive && (
                  <PaymentMethodConfig
                    paymentMethod={
                      paymentMethod as OrdersAutoCancelPaymentMethod
                    }
                    expirationTime={config?.expirationTime ?? null}
                    onExpirationTimeChange={handleExpirationTimeChange}
                    hasError={paymentMethodsWithErrorSet.has(
                      paymentMethod as OrdersAutoCancelPaymentMethod,
                    )}
                    disabled={disabled}
                    shouldFocus={focusedPaymentMethods.has(
                      paymentMethod as OrdersAutoCancelPaymentMethod,
                    )}
                  />
                )}
              </Box>
            ),
          )}
          {selectedSubceedsCheckout && (
            <AlertSessionStorage
              appearance="neutral"
              keyName="autoCancelCheckoutAlert"
            >
              <Text>{t('checkoutAlert.description')}</Text>
              <Link as="a" href={url} target="_blank">
                <Box display="flex" gap="2">
                  {t('checkoutAlert.link')}
                  <Icon source={<ExternalLinkIcon />} />
                </Box>
              </Link>
            </AlertSessionStorage>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}

export default PaymentMethodsConfig;
